import React from "react";
import { Link } from "react-router-dom";
import {
  Table,
  Space,
  Popconfirm,
  Popover,
  Tag,
  Button,
  List,
  Modal,
  message,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { ListComponent, BaseListComponentProps } from "../BaseComponent";
import {
  SearchRequest,
  AdminUserDeleteRequest,
  AdminUserResetPasswordRequest,
} from "../../services/api/interfaces/ApiRequest";
import { Pagination } from "components/Pagination";

type AdminUserListProps = BaseListComponentProps & {
  loading: boolean;
  users: any;
  pagination: any;

  history: any;
  searchOptions: any;

  loadAdminUsers: (args: SearchRequest) => void;

  adminUserDelete: (args: AdminUserDeleteRequest) => void;
  adminUserResetPassword: (args: AdminUserResetPasswordRequest) => Promise<any>;
};

export default class AdminUserList extends ListComponent<AdminUserListProps> {
  static defaultProps = {
    loading: false,

    loadAdminUsers: () => {},
  };

  tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any) =>
        `${record.first_name} ${record.last_name}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.email - b.email,
    },
    {
      title: "Roles",
      dataIndex: "roles",
      key: "roles",
      responsive: ["md"] as any,
      sorter: (a: any, b: any) => a.roles?.length - b.roles?.length,
      render: (text: string, record: any) => {
        const popoverContent = (
          <div style={{ height: 200, overflow: "auto" }}>
            <List
              dataSource={record.roles?.map((p) => p.item_name) || []}
              renderItem={(item: any) => <List.Item>{item}</List.Item>}
              bordered
            />
          </div>
        );

        return (
          <Popover
            content={popoverContent}
            title="Roles"
            trigger="hover"
            placement="bottom"
          >
            <Tag color="geekblue">{record.roles?.length || 0}</Tag>
          </Popover>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      align: "right",
      render: (text: string, record: any) => (
        <Space size="middle">
          <Link
            to={`user/${record.uuid}`}
            ref={(refs) => this.actionRefs.edit.push(refs)}
          >
            <Button type="dashed" shape="round" icon={<EditOutlined />} />
          </Link>

          <div ref={(refs) => this.actionRefs.delete.push(refs)}>
            <Popconfirm
              title="Are you sure about deleting this user?"
              placement="topLeft"
              onConfirm={() => this.handleDelete(record.uuid)}
            >
              <Button type="dashed" shape="round" icon={<DeleteOutlined />} />
            </Popconfirm>
          </div>

          <Button
            type="dashed"
            shape="round"
            icon={<SettingOutlined />}
            onClick={() =>
              this.handleResetPassword(record.uuid, record.first_name)
            }
          />
        </Space>
      ),
    },
  ] as any;

  protected load() {
    this.searchOptions.search = {
      ...this.searchOptions.search,
      ...this.props.searchOptions.search,
    };

    this.props.loadAdminUsers(this.searchOptions);
  }

  handleDelete = async (uuid: string) => {
    const { adminUserDelete } = this.props;

    await adminUserDelete({
      admin_user_uuid: uuid,
    });

    message.success("User deleted successfully!");
  };

  handleResetPassword = (uuid: string, firstName: string | null) => {
    const { adminUserResetPassword } = this.props;

    Modal.confirm({
      title: `Do you want to reset ${
        firstName ? `${firstName}'s` : "this user"
      } password?`,
      icon: <ExclamationCircleOutlined />,
      content: "The new password will be delivery by email.",
      async onOk() {
        try {
          await adminUserResetPassword({
            admin_user_uuid: uuid,
          });

          message.success(
            `${
              firstName ? `${firstName}'s` : "User"
            } password reset successfully!`
          );
        } catch (error) {
          message.error(`Could not reset password. Reason: ${error}`);
        }
      },
    });
  };

  render() {
    const { loading, pagination, users, loadAdminUsers } = this.props;

    return (
      <>
        <Table
          loading={loading}
          rowKey="uuid"
          columns={this.tableColumns}
          pagination={false}
          dataSource={users}
          onChange={(pagination: any, filters: any, sorter: any) =>
            this.handleTableChange(pagination, sorter, loadAdminUsers)
          }
        />
        <Pagination
          loading={loading}
          current={pagination?.page}
          pageSize={pagination?.limit}
          length={users?.length}
          onChange={(pagination) =>
            this.handleTableChange(pagination, undefined, loadAdminUsers)
          }
        />
      </>
    );
  }
}
